import React, { useState, useEffect } from 'react';
import { updateSettings } from '../../api';

function AccountSection({ settings, setSettings, setMessage, licenseStatus, onLicenseChange }) {
  // ══════ 1. ALL useState ══════
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [licenseKey, setLicenseKey] = useState('');
  const [activating, setActivating] = useState(false);

  // Lizenz-Key automatisch formatieren: LUHM-XXXXX-XXXXX-XXXXX-XXXXX
  const handleLicenseKeyChange = (e) => {
    let val = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    // Auto-Bindestriche nach LUHM und jeder 5er-Gruppe
    const raw = val.replace(/-/g, '');
    if (raw.length > 4) {
      const prefix = raw.slice(0, 4); // LUHM
      const rest = raw.slice(4);
      const groups = rest.match(/.{1,5}/g) || [];
      val = prefix + '-' + groups.join('-');
    }
    if (val.length > 24) val = val.slice(0, 24); // Max: LUHM-XXXXX-XXXXX-XXXXX-XXXXX
    setLicenseKey(val);
  };

  const handleActivate = async () => {
    if (!licenseKey.trim() || !window.electronAPI?.license) return;
    setActivating(true);
    setMessage(null);
    try {
      const result = await window.electronAPI.license.activate(licenseKey.trim());
      if (result.success) {
        setMessage({ type: 'success', text: `${result.license.typeLabel} aktiviert — gültig bis ${result.license.expiresFormatted}` });
        setLicenseKey('');
        onLicenseChange?.();
      } else {
        setMessage({ type: 'error', text: result.error || 'Lizenzschlüssel ungültig.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler bei der Aktivierung.' });
    }
    setActivating(false);
  };

  const handleDeactivate = async () => {
    if (!window.electronAPI?.license) return;
    if (!window.confirm('Lizenz auf diesem Gerät deaktivieren?\n\nDu kannst den Key danach auf einem anderen Gerät verwenden.')) return;
    setMessage(null);
    try {
      const result = await window.electronAPI.license.deactivate();
      setMessage({ type: 'success', text: result.message });
      onLicenseChange?.();
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Deaktivieren.' });
    }
  };

  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) return;
    setSaving(true);
    setMessage(null);
    try {
      await updateSettings({ readwise_api_key: apiKey.trim() });
      setMessage({ type: 'success', text: 'API-Key gespeichert.' });
      setApiKey('');
      setShowApiKey(false);
      const { fetchSettings } = await import('../../api');
      const updated = await fetchSettings();
      setSettings(updated);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Speichern.' });
    }
    setSaving(false);
  };

  const status = licenseStatus;
  const isElectron = !!window.electronAPI?.license;

  return (
    <>
      {/* ═══ Lizenz-Bereich ═══ */}
      {isElectron && (
        <section className="settings-section">
          <h3 className="settings-section-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Lizenz
          </h3>

          {/* Status-Anzeige */}
          {status && (
            <div className={`settings-license-status settings-license-${status.state}`}>
              <div className="settings-license-status-row">
                <span className={`settings-license-badge ${status.state}`}>
                  {status.state === 'licensed' && 'Aktiv'}
                  {status.state === 'expired' && 'Abgelaufen'}
                  {status.state === 'trial' && 'Testversion'}
                  {status.state === 'trial_expired' && 'Abgelaufen'}
                </span>
                <span className="settings-license-message">{status.message}</span>
              </div>

              {/* Trial-Fortschrittsbalken */}
              {status.state === 'trial' && (
                <div className="settings-license-trial-bar">
                  <div
                    className="settings-license-trial-fill"
                    style={{ width: `${((14 - status.trialDaysLeft) / 14) * 100}%` }}
                  />
                </div>
              )}

              {/* Maskierter Key bei aktiver Lizenz */}
              {(status.state === 'licensed' || status.state === 'expired') && status.license && (
                <div className="settings-api-key-display" style={{ marginTop: 8 }}>
                  <span className="settings-api-key-value">
                    {status.license.key.slice(0, 9)}•••••-•••••{status.license.key.slice(-5)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Key-Eingabe: bei Trial, Trial-Expired oder Expired */}
          {status && status.state !== 'licensed' && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <div className="settings-input-row">
                <input
                  type="text"
                  className="settings-input"
                  value={licenseKey}
                  onChange={handleLicenseKeyChange}
                  placeholder="LUHM-XXXXX-XXXXX-XXXXX-XXXXX"
                  style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}
                  onKeyDown={e => { if (e.key === 'Enter' && licenseKey.length === 24) handleActivate(); }}
                />
                <button
                  className="settings-btn-primary"
                  onClick={handleActivate}
                  disabled={activating || licenseKey.length < 24}
                >
                  {activating ? 'Prüfe…' : 'Aktivieren'}
                </button>
              </div>
            </div>
          )}

          {/* Kaufen-Button bei Trial-Expired */}
          {status && (status.state === 'trial_expired' || status.state === 'expired') && (
            <a
              href="https://luhm.app"
              target="_blank"
              rel="noopener noreferrer"
              className="settings-btn-primary"
              style={{ display: 'inline-block', marginTop: 'var(--space-3)', textDecoration: 'none', textAlign: 'center' }}
            >
              Jetzt freischalten
            </a>
          )}

          {/* Deaktivieren-Button bei aktiver Lizenz */}
          {status && status.state === 'licensed' && (
            <button
              className="settings-btn-secondary"
              style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)' }}
              onClick={handleDeactivate}
            >
              Lizenz deaktivieren
            </button>
          )}
        </section>
      )}

      {/* ═══ Trennlinie zwischen Lizenz und Readwise ═══ */}
      {isElectron && <div style={{ borderTop: '1px solid var(--color-divider)', margin: 'var(--space-2) 0 var(--space-4)' }} />}

      {/* ═══ Onboarding-Banner ═══ */}
      {!settings.readwise?.has_api_key && (
        <div className="settings-onboarding">
          <div className="settings-onboarding-icon">👋</div>
          <div>
            <strong>Willkommen im Zettelkasten!</strong>
            <p>Um deine Readwise-Bibliothek zu verbinden, trage unten deinen API-Key ein. Den Key findest du unter <strong>readwise.io/access-token</strong>.</p>
            <p>Du kannst den Zettelkasten auch ohne Readwise-Anbindung nutzen — der Bereich „Bibliothek" bleibt dann leer.</p>
          </div>
        </div>
      )}

      {/* ═══ Readwise API Key ═══ */}
      <section className="settings-section">
        <h3 className="settings-section-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
          </svg>
          Readwise API-Key
        </h3>
        <p className="settings-desc">
          Verbindet deine Readwise-Bibliothek mit dem Zettelkasten.
        </p>
        <div className="settings-api-key-display">
          <span className="settings-api-key-value">
            {settings.readwise?.has_api_key ? settings.readwise.api_key_masked : 'Kein API-Key gesetzt'}
          </span>
          {settings.readwise?.has_api_key && (
            <span className="settings-api-key-badge">Aktiv</span>
          )}
        </div>
        <div className="settings-input-row">
          <input
            type={showApiKey ? 'text' : 'password'}
            className="settings-input"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="Neuen API-Key eingeben..."
          />
          <button className="settings-btn-icon" onClick={() => setShowApiKey(!showApiKey)} title={showApiKey ? 'Verbergen' : 'Anzeigen'}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              {showApiKey ? (
                <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
              ) : (
                <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
              )}
            </svg>
          </button>
          <button className="settings-btn-primary" onClick={handleSaveApiKey} disabled={saving || !apiKey.trim()}>
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </section>
    </>
  );
}

export default AccountSection;
